import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import CertificateTemplate from "../CertificateTemplate/CertificateTemplate";

import "./VerifyCertificate.css";

const VerifyCertificate = () => {
  const { certId } = useParams();
  const [cert, setCert] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    setLoading(true);
    setError("");
    fetch(`/api/certificates/${certId}`)
      .then((res) => {
        if (!res.ok) throw new Error("Certificate not found");
        return res.json();
      })
      .then((data) => setCert(data))
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [certId]);

  // Inline styles
  const styles = {
    container: {
      minHeight: "100vh",
      backgroundColor: "#f8f9fa",
      padding: "40px 16px",
    },
    innerContainer: {
      maxWidth: "768px",
      margin: "0 auto",
      paddingTop: "16px",
    },
    loadingContainer: {
      minHeight: "100vh",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      backgroundColor: "#f8f9fa",
    },
    loadingContent: {
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      gap: "12px",
    },
    spinner: {
      width: "40px",
      height: "40px",
      border: "4px solid #e9ecef",
      borderTop: "4px solid #0d6efd",
      borderRadius: "50%",
      animation: "spin 1s linear infinite",
    },
    loadingText: {
      color: "#6c757d",
      fontSize: "14px",
    },
    errorContainer: {
      minHeight: "100vh",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      backgroundColor: "#f8f9fa",
      padding: "0 16px",
    },
    errorCard: {
      backgroundColor: "#ffffff",
      border: "1px solid #f8d7da",
      borderRadius: "16px",
      boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
      padding: "32px",
      maxWidth: "448px",
      width: "100%",
      textAlign: "center",
    },
    errorIcon: {
      margin: "0 auto 16px",
      width: "56px",
      height: "56px",
      borderRadius: "50%",
      backgroundColor: "#f8d7da",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    },
    errorIconSvg: {
      width: "28px",
      height: "28px",
      color: "#dc3545",
    },
    errorTitle: {
      fontSize: "18px",
      fontWeight: "600",
      color: "#dc3545",
      marginBottom: "4px",
    },
    errorText: {
      color: "#6c757d",
      fontSize: "14px",
    },
    verifiedSection: {
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      textAlign: "center",
      marginBottom: "32px",
    },
    verifiedIcon: {
      width: "64px",
      height: "64px",
      borderRadius: "50%",
      backgroundColor: "#d4edda",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      marginBottom: "12px",
    },
    verifiedIconSvg: {
      width: "36px",
      height: "36px",
      color: "#198754",
    },
    verifiedTitle: {
      fontSize: "24px",
      fontWeight: "700",
      color: "#198754",
    },
    verifiedSubtext: {
      color: "#6c757d",
      fontSize: "14px",
      marginTop: "4px",
    },
    detailsCard: {
      backgroundColor: "#ffffff",
      borderRadius: "16px",
      boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
      border: "1px solid #e9ecef",
      padding: "32px 40px",
      marginBottom: "32px",
    },
    detailGrid: {
      display: "grid",
      gridTemplateColumns: "repeat(2, 1fr)",
      gap: "20px 24px",
    },
    detailFull: {
      gridColumn: "span 2",
    },
    detailLabel: {
      fontSize: "12px",
      textTransform: "uppercase",
      letterSpacing: "0.05em",
      color: "#adb5bd",
      fontWeight: "500",
      marginBottom: "4px",
    },
    detailValue: {
      color: "#212529",
      fontWeight: "600",
    },
    previewCard: {
      backgroundColor: "#ffffff",
      borderRadius: "16px",
      boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
      border: "1px solid #e9ecef",
      padding: "24px 32px",
    },
    previewLabel: {
      fontSize: "12px",
      textTransform: "uppercase",
      letterSpacing: "0.05em",
      color: "#adb5bd",
      fontWeight: "500",
      marginBottom: "12px",
    },
    previewContent: {
      overflowX: "auto",
    },
  };

  if (loading) {
    return (
      <div style={styles.loadingContainer}>
        <div style={styles.loadingContent}>
          <div style={styles.spinner} />
          <p style={styles.loadingText}>Verifying certificate…</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={styles.errorContainer}>
        <div style={styles.errorCard}>
          <div style={styles.errorIcon}>
            <svg
              style={styles.errorIconSvg}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </div>
          <h4 style={styles.errorTitle}>Invalid Certificate</h4>
          <p style={styles.errorText}>{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.innerContainer}>
        {/* Verified badge */}
        <div style={styles.verifiedSection}>
          <div style={styles.verifiedIcon}  className="verified-icon">
            <svg
              style={styles.verifiedIconSvg}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M5 13l4 4L19 7"
              />
            </svg>
          </div>
          <h4 style={styles.verifiedTitle}>Certificate Verified</h4>
          <p style={styles.verifiedSubtext}>
            This certificate is authentic and issued by DTECH.
          </p>
        </div>

        {/* Details card */}
        <div style={styles.detailsCard}>
          <dl style={styles.detailGrid}>
            <DetailItem label="Student Name" value={cert.studentName} />
            <DetailItem label="Certificate No." value={cert.certNo} />
            <DetailItem label="Course" value={cert.course} />
            <DetailItem label="Branch" value={cert.branch} />
            <DetailItem label="Centre" value={cert.centre} />
            <DetailItem label="Awarded On" value={formatDate(cert.awardedOn)} />
            <DetailItem
              label="Internship Duration"
              value={`${formatDate(cert.startDate)} — ${formatDate(cert.endDate)}`}
              full
            />
          </dl>
        </div>

        {/* Certificate preview */}
        {/* <div style={styles.previewCard}>
          <p style={styles.previewLabel}>Certificate Preview</p>
          <div style={styles.previewContent}>
            <CertificateTemplate data={cert} />
          </div>
        </div> */}
      </div>

      {/* Add keyframes for spinner animation */}
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
    </div>
  );
};

const DetailItem = ({ label, value, full }) => {
  const styles = {
    detailFull: {
      gridColumn: "span 2",
    },
    detailLabel: {
      fontSize: "12px",
      textTransform: "uppercase",
      letterSpacing: "0.05em",
      color: "#adb5bd",
      fontWeight: "500",
      marginBottom: "4px",
    },
    detailValue: {
      color: "#212529",
      fontWeight: "600",
    },
  };

  return (
    <div style={full ? styles.detailFull : {}}>
      <dt style={styles.detailLabel}>{label}</dt>
      <dd style={styles.detailValue}>{value || "—"}</dd>
    </div>
  );
};

const formatDate = (dateStr) => {
  if (!dateStr) return "—";
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return dateStr;
  return d.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

export default VerifyCertificate;
